import {Helmet} from '@dr.pogodin/react-helmet';
import {Card, DataStatus, Form, FormField, FormSubmitHandler} from 'platform/components';
import {Box, Heading, Space, VStack} from 'platform/foundation';

import {useParams} from 'react-router-dom';

import {defaultTo, isNil, not, pipe, reject} from 'ramda';
import {isNumber} from 'ramda-adjunct';

import {useGetTenantQuery} from '@dms/api/core';
import {
  useGetSettingsServiceOrderVariantsQuery,
  useGetSmallUnitMaterialApiQuery,
  usePatchSmallUnitMaterialApiMutation,
  usePostSmallUnitMaterialApiMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {
  SettingsFooter,
  SettingsSection,
  SettingsTemplate,
  SettingsTemplateHeader,
} from '@dms/settings/shared';
import {handleApiError, useCurrencies, useTenant} from '@dms/shared';

import {ApiError, useNavigate} from 'shared/web';

import {
  AMOUNT_TYPE_OPTIONS,
  WorkshopSmallUnitMaterialDetailFormType,
  workshopSmallUnitMaterialDetailSchema,
} from './schemas/WorkshopSmallUnitMaterialDetailSchema';

const PERCENTAGE_BASE = 100;

const rejectNullish = pipe<[(string | null)[] | undefined], (string | null)[], string[]>(
  defaultTo([]),
  reject(isNil)
);

export function WorkshopSmallUnitMaterialDetail() {
  const {id} = useParams();
  const navigate = useNavigate();
  const {getCurrencySymbol} = useCurrencies();
  const {tenantCurrency} = useTenant();

  const isCreating = isNil(id);

  const {data: {currency: defaultCurrency} = {}} = useGetTenantQuery();
  const {data, isLoading, isError} = useGetSmallUnitMaterialApiQuery(
    {
      smallUnitMaterialId: id ?? '',
    },
    {skip: isCreating}
  );
  const [patchSmallUnitMaterial] = usePatchSmallUnitMaterialApiMutation();
  const [postSmallUnitMaterial] = usePostSmallUnitMaterialApiMutation();

  const {data: orderVariantsOptions, isLoading: isOrderVariantsLoading} =
    useGetSettingsServiceOrderVariantsQuery();

  const handleSubmit: FormSubmitHandler<WorkshopSmallUnitMaterialDetailFormType> = async (
    data,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    const {amountValue, maxValue, ...restBody} = data;

    const body = {
      ...restBody,
      maxValue: maxValue
        ? {
            currency: tenantCurrency,
            amount: maxValue,
          }
        : undefined,
      amountValue: amountValue / PERCENTAGE_BASE,
    };

    const request = isCreating
      ? postSmallUnitMaterial({body})
      : patchSmallUnitMaterial({smallUnitMaterialId: id, body});

    await request
      .unwrap()
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.workshopSmallUnitMaterial);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const amountOptions = [
    {
      label: i18n.t('entity.workshopMinorUnitMaterial.labels.unitFromMaterial'),
      value: AMOUNT_TYPE_OPTIONS[0],
    },
    {
      label: i18n.t('entity.workshopMinorUnitMaterial.labels.unitFromLabour'),
      value: AMOUNT_TYPE_OPTIONS[1],
    },
    {
      label: i18n.t('entity.workshopMinorUnitMaterial.labels.unitFromMaterialAndLabour'),
      value: AMOUNT_TYPE_OPTIONS[2],
    },
  ];

  const titleKey = isCreating
    ? 'page.settings.actions.newSmallUnitMaterial'
    : 'entity.workshopMinorUnitMaterial.labels.minorUnitMaterial';

  const header: SettingsTemplateHeader = {
    title: isCreating
      ? i18n.t('page.settings.actions.newSmallUnitMaterial')
      : i18n.t('entity.workshopMinorUnitMaterial.labels.minorUnitMaterial'),
    breadcrumbs: [
      {
        label: i18n.t('entity.workshopMinorUnitMaterial.labels.minorUnitMaterial'),
        href: settingsRoutes.workshopSmallUnitMaterial,
      },
    ],
  };

  return (
    <>
      <Helmet title={i18n.t(titleKey)} />
      <SettingsTemplate
        header={header}
        data-testid={testIds.settings.workshopSmallUnitMaterialDetail('page')}
      >
        <DataStatus isLoading={isLoading} isError={isError}>
          <Form<WorkshopSmallUnitMaterialDetailFormType>
            experimentalZodSchema={workshopSmallUnitMaterialDetailSchema}
            onSubmit={handleSubmit}
            shouldWatchForUnsavedChanges
            defaultValues={{
              name: data?.name ?? '',
              amountType: data?.amountType ?? 'SMALLUNITMATERIAL_FROM_MATERIAL',
              amountValue: isNumber(data?.amountValue)
                ? Number(data?.amountValue) * PERCENTAGE_BASE
                : 0,
              isMaxValue: data?.isMaxValue ?? false,
              maxValue: data?.maxValue?.amount ?? undefined,
              orderVariantIds: rejectNullish(data?.orderVariantIds),
            }}
          >
            {(control, formApi) => (
              <>
                <SettingsSection>
                  <VStack spacing={4}>
                    <FormField
                      control={control}
                      name="name"
                      type="text"
                      label={i18n.t('general.labels.name')}
                      isRequired
                      data-testid={testIds.settings.workshopSmallUnitMaterialDetail('name')}
                    />
                    <FormField
                      control={control}
                      name="orderVariantIds"
                      type="multiChoice"
                      options={orderVariantsOptions ?? []}
                      isLoading={isOrderVariantsLoading}
                      label={i18n.t('entity.workshopMinorUnitMaterial.labels.orderVariant')}
                      helperText={i18n.t(
                        'entity.workshopMinorUnitMaterial.labels.orderVariantHelper'
                      )}
                      isRequired
                      isNotClearable
                      data-testid={testIds.settings.workshopSmallUnitMaterialDetail('orderVariant')}
                    />
                    <Card variant="inlineGrey">
                      <VStack spacing={4}>
                        <Heading size={4}>{i18n.t('general.labels.amount')}</Heading>
                        <FormField
                          control={control}
                          name="amountType"
                          type="radio"
                          direction="column"
                          spacing={3}
                          options={amountOptions}
                          data-testid={testIds.settings.workshopSmallUnitMaterialDetail(
                            'amountType'
                          )}
                        />
                        <Box width={82}>
                          <FormField
                            control={control}
                            name="amountValue"
                            type="number"
                            isStepperVisible
                            minStepperValue={0}
                            maxStepperValue={100}
                            data-testid={testIds.settings.workshopSmallUnitMaterialDetail(
                              'amountValue'
                            )}
                          />
                        </Box>
                      </VStack>
                    </Card>
                    <FormField
                      control={control}
                      name="isMaxValue"
                      type="checkbox"
                      label={i18n.t('entity.workshopMinorUnitMaterial.labels.enableMaximumAmount', {
                        currency: getCurrencySymbol(defaultCurrency),
                      })}
                      onChange={() => formApi.setValue('maxValue', null)}
                      data-testid={testIds.settings.workshopSmallUnitMaterialDetail('isMaxValue')}
                    />
                    <Box width={92}>
                      <FormField
                        control={control}
                        name="maxValue"
                        type="number"
                        isDisabled={not(formApi.watch('isMaxValue'))}
                        helperText={i18n.t(
                          'entity.workshopMinorUnitMaterial.labels.amountMustNotExceedAmountSelectedPerOrder'
                        )}
                        minStepperValue={0}
                        data-testid={testIds.settings.workshopSmallUnitMaterialDetail('maxValue')}
                      />
                    </Box>
                  </VStack>
                </SettingsSection>
                <Space vertical={20} />
                <SettingsFooter
                  actions={[
                    {
                      control,
                      type: 'form-button',
                      buttonType: 'submit',
                      title: isCreating
                        ? i18n.t('general.actions.create')
                        : i18n.t('general.actions.saveChanges'),
                    },
                  ]}
                  data-testid={testIds.settings.workshopSmallUnitMaterialDetail('saveChanges')}
                />
              </>
            )}
          </Form>
        </DataStatus>
      </SettingsTemplate>
    </>
  );
}
